/**
 * Tag Editor Insertion Service
 * Handles tag insertion into editor
 */

import { Injectable } from '@angular/core'
import {
  COMPONENT_STRINGS,
  DEBUG_PREVIEW_LENGTHS,
  DOM_ELEMENT_NAMES,
  HTML_ATTRIBUTES,
  HTML_VALUES,
  LIST_TYPES,
  ListType,
  NODE_TYPES,
  TAG_EDITOR_STRINGS,
} from '../tag-editor.constants'
import { TagManagerService } from './tag-manager.service'
import { TagEditorStateService } from './tag-editor-state.service'
import { DebugLoggingService } from './debug-logging.service'
import { EditorInteractionService } from './editor-interaction.service'
import { TagEditorTreeNode } from '../tag-editor-tree-node'
import { InsertedTagCustom } from '../tag-editor.model'
import { generateWCAGCompliantTagColors } from '../../../../../shared/helpers/color.utils'

/**
 * Callbacks for tag insertion operations
 */
export interface InsertionCallbacks {
  readonly onBeforeInsert: () => void
  readonly onAfterInsert: (tagElement: HTMLElement) => void
  readonly onTagsUpdated: (tags: ReadonlyArray<InsertedTagCustom>) => void
  readonly onContentChanged: (content: string) => void
  readonly onDebouncedContentChanged: (content: string) => void
  readonly onMarkForCheck: () => void
  readonly getEditorHTML: () => string
}

@Injectable({ providedIn: 'root' })
export class TagEditorInsertionService {
  constructor(
    private readonly tagManagerService: TagManagerService,
    private readonly stateService: TagEditorStateService,
    private readonly debugLoggingService: DebugLoggingService,
    private readonly editorInteractionService: EditorInteractionService,
  ) {}

  private debugLog(message: string, ...args: ReadonlyArray<unknown>): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, message, ...args)
  }

  private insertTagAroundListItemsInOriginalList(
    clonedContent: DocumentFragment,
    range: Range,
    node: TagEditorTreeNode,
    editor: HTMLElement,
    callbacks: InsertionCallbacks,
  ): void {
    this.debugLog(' Inserting tag around list items in original list to avoid nested lists')

    const commonAncestor: Node = range.commonAncestorContainer
    let originalList: HTMLElement | null = null

    if (commonAncestor.nodeType === NODE_TYPES.ELEMENT_NODE) {
      const ancestorElement: HTMLElement = commonAncestor as HTMLElement
      const ancestorTagName: string = ancestorElement.tagName.toUpperCase()
      if (
        ancestorTagName === DOM_ELEMENT_NAMES.ORDERED_LIST.toUpperCase() ||
        ancestorTagName === DOM_ELEMENT_NAMES.UNORDERED_LIST.toUpperCase()
      ) {
        originalList = ancestorElement
      }
    }

    if (!originalList) {
      this.debugLog(' Could not find original list, falling back to standard insertion')
      const tagId: string = this.tagManagerService.generateTagId()
      const wcagColors: { readonly foreground: string; readonly background: string } = generateWCAGCompliantTagColors(
        node.label,
      )
      const tagElement: HTMLElement = this.tagManagerService.createTagElement(
        tagId,
        node.label,
        clonedContent,
        wcagColors.foreground,
        node.data?.categoryName,
        node.data?.categoryId,
      )
      range.deleteContents()
      range.insertNode(tagElement)
      tagElement.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)
      callbacks.onContentChanged(callbacks.getEditorHTML())
      queueMicrotask(() => {
        const tags = this.tagManagerService.parseTagsFromContent(editor)
        callbacks.onTagsUpdated(tags)
        callbacks.onMarkForCheck()
        this.stateService.setInsertingTag(false)
        this.stateService.setTagSelectorOpen(false)
      })
      return
    }

    const clonedListItems: HTMLElement[] = []
    for (let i = 0; i < clonedContent.childNodes.length; i++) {
      const node: Node = clonedContent.childNodes[i]
      if (node.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const element: HTMLElement = node as HTMLElement
        if (element.tagName.toUpperCase() === DOM_ELEMENT_NAMES.LIST_ITEM.toUpperCase()) {
          clonedListItems.push(element)
        }
      }
    }

    if (clonedListItems.length === 0) {
      this.debugLog(' No list items found in cloned content')
      this.stateService.setInsertingTag(false)
      return
    }

    const allListItems: HTMLElement[] = Array.from(originalList.querySelectorAll(DOM_ELEMENT_NAMES.LIST_ITEM))

    const selectedListItems: HTMLElement[] = []
    const INVALID_INDEX = -1
    let startIndex: number = INVALID_INDEX
    for (let i = 0; i < allListItems.length; i++) {
      if (range.intersectsNode(allListItems[i])) {
        startIndex = i
        break
      }
    }

    if (startIndex === INVALID_INDEX) {
      this.debugLog(' Could not find starting index of selection in original list')
      this.stateService.setInsertingTag(false)
      return
    }

    for (let i = 0; i < clonedListItems.length && startIndex + i < allListItems.length; i++) {
      const clonedItem: HTMLElement = clonedListItems[i]
      const originalItem: HTMLElement = allListItems[startIndex + i]

      const clonedText: string = (clonedItem.textContent || TAG_EDITOR_STRINGS.EMPTY_STRING).trim()
      const originalText: string = (originalItem.textContent || TAG_EDITOR_STRINGS.EMPTY_STRING).trim()

      if (clonedText === originalText || originalText.includes(clonedText) || clonedText.includes(originalText)) {
        selectedListItems.push(originalItem)
      } else {
        this.debugLog(' List item mismatch:', {
          clonedText: clonedText.substring(0, DEBUG_PREVIEW_LENGTHS.TEXT_MISMATCH_PREVIEW),
          originalText: originalText.substring(0, DEBUG_PREVIEW_LENGTHS.TEXT_MISMATCH_PREVIEW),
        })
      }
    }

    if (selectedListItems.length === 0) {
      this.debugLog(' No matching list items found')
      this.stateService.setInsertingTag(false)
      return
    }

    this.debugLog(' Wrapping list items in tags:', { count: selectedListItems.length })

    const wcagColors: { readonly foreground: string; readonly background: string } = generateWCAGCompliantTagColors(
      node.label,
    )

    for (let i = selectedListItems.length - 1; i >= 0; i--) {
      const listItem: HTMLElement = selectedListItems[i]
      const listItemContent: DocumentFragment = document.createDocumentFragment()
      while (listItem.firstChild) {
        listItemContent.appendChild(listItem.firstChild)
      }

      const tagId: string = this.tagManagerService.generateTagId()
      const tagElement: HTMLElement = this.tagManagerService.createTagElement(
        tagId,
        node.label,
        listItemContent,
        wcagColors.foreground,
        node.data?.categoryName,
        node.data?.categoryId,
      )

      listItem.appendChild(tagElement)
      tagElement.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)
    }
    callbacks.onContentChanged(callbacks.getEditorHTML())
    queueMicrotask(() => {
      const tags = this.tagManagerService.parseTagsFromContent(editor)
      callbacks.onTagsUpdated(tags)
      callbacks.onMarkForCheck()
      this.debugLog(' Tags updated after list item wrapping, count:', tags.length)
      this.stateService.setInsertingTag(false)
      this.stateService.setTagSelectorOpen(false)
    })
  }

  private containsOnlyListItems(clonedContent: DocumentFragment): boolean {
    if (clonedContent.childNodes.length === 0) {
      return false
    }

    for (let i = 0; i < clonedContent.childNodes.length; i++) {
      const node: Node = clonedContent.childNodes[i]
      if (node.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const element: HTMLElement = node as HTMLElement
        const tagName: string = element.tagName.toUpperCase()
        if (tagName !== DOM_ELEMENT_NAMES.LIST_ITEM.toUpperCase()) {
          return false
        }
      } else {
        return false
      }
    }

    return true
  }

  private normalizeClonedContentForListItems(clonedContent: DocumentFragment, range: Range): DocumentFragment | Node {
    if (this.containsOnlyListItems(clonedContent)) {
      this.debugLog(
        ' Cloned content contains only list items from existing list - returning as-is to avoid nested lists:',
        {
          listItemCount: clonedContent.childNodes.length,
        },
      )
      return clonedContent
    }

    const listItems: HTMLElement[] = []
    for (let i = 0; i < clonedContent.childNodes.length; i++) {
      const node: Node = clonedContent.childNodes[i]
      if (node.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const element: HTMLElement = node as HTMLElement
        const tagName: string = element.tagName.toUpperCase()
        if (tagName === DOM_ELEMENT_NAMES.LIST_ITEM.toUpperCase()) {
          listItems.push(element)
        }
      }
    }

    if (listItems.length === 0) {
      return clonedContent
    }

    this.debugLog(' Detected list items mixed with other content:', {
      listItemCount: listItems.length,
      totalNodes: clonedContent.childNodes.length,
    })

    let listType: ListType = LIST_TYPES.UNORDERED
    const commonAncestor: Node = range.commonAncestorContainer

    if (commonAncestor.nodeType === NODE_TYPES.ELEMENT_NODE) {
      const ancestorElement: HTMLElement = commonAncestor as HTMLElement
      const ancestorTagName: string = ancestorElement.tagName.toUpperCase()
      if (ancestorTagName === DOM_ELEMENT_NAMES.ORDERED_LIST.toUpperCase()) {
        listType = LIST_TYPES.ORDERED
      } else if (ancestorTagName === DOM_ELEMENT_NAMES.UNORDERED_LIST.toUpperCase()) {
        listType = LIST_TYPES.UNORDERED
      } else {
        if (listItems.length > 0) {
          let parent: Node | null = listItems[0].parentNode
          while (parent && parent !== commonAncestor) {
            if (parent.nodeType === NODE_TYPES.ELEMENT_NODE) {
              const parentElement: HTMLElement = parent as HTMLElement
              const parentTagName: string = parentElement.tagName.toUpperCase()
              if (parentTagName === DOM_ELEMENT_NAMES.ORDERED_LIST.toUpperCase()) {
                listType = LIST_TYPES.ORDERED
                break
              } else if (parentTagName === DOM_ELEMENT_NAMES.UNORDERED_LIST.toUpperCase()) {
                listType = LIST_TYPES.UNORDERED
                break
              }
            }
            parent = parent.parentNode
          }
        }
      }
    }

    const listContainer: HTMLElement = document.createElement(listType)
    const allNodes: Node[] = Array.from(clonedContent.childNodes)
    const otherNodes: Node[] = []
    for (const node of allNodes) {
      if (node.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const element: HTMLElement = node as HTMLElement
        const tagName: string = element.tagName.toUpperCase()
        if (tagName === DOM_ELEMENT_NAMES.LIST_ITEM.toUpperCase()) {
          listContainer.appendChild(node)
        } else {
          otherNodes.push(node)
        }
      } else {
        otherNodes.push(node)
      }
    }

    const newFragment: DocumentFragment = document.createDocumentFragment()
    newFragment.appendChild(listContainer)
    otherNodes.forEach((node: Node) => {
      newFragment.appendChild(node)
    })
    this.debugLog(' Wrapped list items in container (with other nodes):', {
      listType,
      listItemCount: listContainer.childNodes.length,
      otherNodeCount: otherNodes.length,
    })
    return newFragment
  }

  public insertTag(node: TagEditorTreeNode, text: string, editor: HTMLElement, callbacks: InsertionCallbacks): void {
    this.debugLog(' insertTag called:', { label: node.label, textLength: text.length })

    this.stateService.setInsertingTag(true)

    const existingEditingTag = this.stateService.getEditingTag()
    if (existingEditingTag) {
      this.debugLog(' Exiting existing edit mode before inserting new tag')
      callbacks.onBeforeInsert()
    }

    const selectionState = this.editorInteractionService.getRichSelectionState()
    if (!selectionState.hasSelection || !selectionState.clonedContent) {
      this.debugLog(' Cannot insert tag - no selection or invalid selection state')
      this.stateService.setInsertingTag(false)
      return
    }

    const selection: Selection | null = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      this.debugLog(' Cannot insert tag - no selection')
      this.stateService.setInsertingTag(false)
      return
    }

    const range: Range = selection.getRangeAt(0)
    const clonedContent: DocumentFragment = selectionState.clonedContent

    this.debugLog(' Using cloned content to preserve formatting:', {
      clonedContentNodes: clonedContent.childNodes.length,
      selectedText: selectionState.selectedText,
    })

    const normalizedContent: DocumentFragment | Node = this.normalizeClonedContentForListItems(clonedContent, range)

    if (normalizedContent instanceof DocumentFragment && this.containsOnlyListItems(normalizedContent)) {
      this.insertTagAroundListItemsInOriginalList(normalizedContent, range, node, editor, callbacks)
      return
    }

    const tagId: string = this.tagManagerService.generateTagId()
    const wcagColors: { readonly foreground: string; readonly background: string } = generateWCAGCompliantTagColors(
      node.label,
    )

    const tagElement: HTMLElement = this.tagManagerService.createTagElement(
      tagId,
      node.label,
      normalizedContent,
      wcagColors.foreground,
      node.data?.categoryName,
      node.data?.categoryId,
    )

    this.debugLog(' Tag element created:', { tagId, label: node.label })

    range.deleteContents()
    range.insertNode(tagElement)
    this.debugLog(' Tag element inserted into DOM')

    const spaceTextNode: Text = document.createTextNode(TAG_EDITOR_STRINGS.NON_BREAKING_SPACE)
    tagElement.parentNode?.insertBefore(spaceTextNode, tagElement.nextSibling)

    tagElement.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)

    const currentContent: string = callbacks.getEditorHTML()
    callbacks.onContentChanged(currentContent)

    queueMicrotask(() => {
      const tags = this.tagManagerService.parseTagsFromContent(editor)
      callbacks.onTagsUpdated(tags)
      callbacks.onMarkForCheck()
      this.debugLog(' Tags updated after insertion, count:', tags.length)

      if (tags.length === 0) {
        this.debugLog(' WARNING: No tags found after insertion, retrying parseAndUpdateTags')
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const retryTags = this.tagManagerService.parseTagsFromContent(editor)
            callbacks.onTagsUpdated(retryTags)
            callbacks.onMarkForCheck()
            this.debugLog(' Retry parseAndUpdateTags completed, count:', retryTags.length)
            callbacks.onContentChanged(callbacks.getEditorHTML())
            this.stateService.setInsertingTag(false)
            this.stateService.setTagSelectorOpen(false)
          })
        })
      } else {
        this.stateService.setInsertingTag(false)
        this.stateService.setTagSelectorOpen(false)
      }
    })

    if (selection) {
      const textNode: Node | null = tagElement.firstChild
      if (textNode && textNode.nodeType === NODE_TYPES.TEXT_NODE) {
        const textLength: number = textNode.textContent?.length || 0
        const insideTagRange: Range = document.createRange()
        insideTagRange.setStart(textNode, textLength)
        insideTagRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(insideTagRange)
      } else {
        const afterTagRange: Range = document.createRange()
        afterTagRange.setStartAfter(tagElement)
        afterTagRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(afterTagRange)
      }
    }

    callbacks.onAfterInsert(tagElement)
  }
}
